import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Copy, Check, Globe, User, Key, FileText } from 'lucide-react';
import { Note, PasswordData } from '../types';
import { useNoteStore } from '../stores/noteStore';

interface PasswordEditorProps {
  note: Note;
}

const PasswordEditor: React.FC<PasswordEditorProps> = ({ note }) => {
  const { updateNote } = useNoteStore();
  
  const [passwordData, setPasswordData] = useState<PasswordData>({
    website: '',
    username: '',
    password: '',
    notes: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  // Load password data from note
  useEffect(() => {
    if (note.passwordData) {
      setPasswordData(note.passwordData);
    }
  }, [note.id, note.passwordData]);

  // Auto-save
  useEffect(() => {
    const timer = setTimeout(() => {
      if (JSON.stringify(passwordData) !== JSON.stringify(note.passwordData)) {
        updateNote(note.id, { passwordData });
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [passwordData, note.id, note.passwordData, updateNote]);

  const handleChange = (field: keyof PasswordData, value: string) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
  };

  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const CopyButton: React.FC<{ text: string; field: string }> = ({ text, field }) => (
    <button
      onClick={() => copyToClipboard(text, field)}
      className={`p-2 rounded-lg transition-all ${
        copiedField === field
          ? 'bg-green-100 text-green-600'
          : 'hover:bg-gray-100 text-gray-500'
      }`}
      title={copiedField === field ? 'Copied!' : 'Copy'}
    >
      {copiedField === field ? (
        <Check className="w-5 h-5" />
      ) : (
        <Copy className="w-5 h-5" />
      )}
    </button>
  );

  return (
    <div className="p-4 space-y-4">
      {/* Website/Service */}
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-600 mb-2">
          <Globe className="w-4 h-4" />
          Website / Service
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={passwordData.website}
            onChange={(e) => handleChange('website', e.target.value)}
            placeholder="e.g., google.com"
            className="flex-1 px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
          />
          {passwordData.website && (
            <CopyButton text={passwordData.website} field="website" />
          )}
        </div>
      </div>

      {/* Username/Email */}
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-600 mb-2">
          <User className="w-4 h-4" />
          Username / Email
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={passwordData.username}
            onChange={(e) => handleChange('username', e.target.value)}
            placeholder="e.g., john@email.com"
            className="flex-1 px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
          />
          {passwordData.username && (
            <CopyButton text={passwordData.username} field="username" />
          )}
        </div>
      </div>

      {/* Password */}
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-600 mb-2">
          <Key className="w-4 h-4" />
          Password
        </label>
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={passwordData.password}
              onChange={(e) => handleChange('password', e.target.value)}
              placeholder="Enter password"
              className="w-full px-4 py-3 pr-12 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent font-mono"
            />
            <button
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700"
              title={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? (
                <EyeOff className="w-5 h-5" />
              ) : (
                <Eye className="w-5 h-5" />
              )}
            </button>
          </div>
          {passwordData.password && (
            <CopyButton text={passwordData.password} field="password" />
          )}
        </div>
        
        {/* Password strength indicator */}
        {passwordData.password && (
          <PasswordStrength password={passwordData.password} />
        )}
      </div>

      {/* Notes */}
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-600 mb-2">
          <FileText className="w-4 h-4" />
          Additional Notes
        </label>
        <textarea
          value={passwordData.notes}
          onChange={(e) => handleChange('notes', e.target.value)}
          placeholder="Security questions, backup codes, etc."
          rows={3}
          className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent resize-none"
        />
      </div>

      {/* Quick Copy All */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="pt-4 border-t border-gray-200"
      >
        <p className="text-xs text-gray-500 text-center mb-3">Quick Copy</p>
        <div className="flex gap-2 justify-center">
          <button
            onClick={() => copyToClipboard(passwordData.username, 'quick-user')}
            disabled={!passwordData.username}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
              copiedField === 'quick-user'
                ? 'bg-green-100 text-green-700'
                : passwordData.username
                ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                : 'bg-gray-50 text-gray-400 cursor-not-allowed'
            }`}
          >
            <User className="w-4 h-4" />
            {copiedField === 'quick-user' ? 'Copied!' : 'Username'}
          </button>
          <button
            onClick={() => copyToClipboard(passwordData.password, 'quick-pass')}
            disabled={!passwordData.password}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
              copiedField === 'quick-pass'
                ? 'bg-green-100 text-green-700'
                : passwordData.password
                ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                : 'bg-gray-50 text-gray-400 cursor-not-allowed'
            }`}
          >
            <Key className="w-4 h-4" />
            {copiedField === 'quick-pass' ? 'Copied!' : 'Password'}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

// Password Strength Component
const PasswordStrength: React.FC<{ password: string }> = ({ password }) => {
  const getStrength = (pwd: string): { score: number; label: string; color: string } => {
    let score = 0;
    if (pwd.length >= 8) score++;
    if (pwd.length >= 12) score++;
    if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) score++;
    if (/\d/.test(pwd)) score++;
    if (/[^a-zA-Z0-9]/.test(pwd)) score++;

    if (score <= 1) return { score: 1, label: 'Weak', color: 'bg-red-500' };
    if (score <= 2) return { score: 2, label: 'Fair', color: 'bg-orange-500' };
    if (score <= 3) return { score: 3, label: 'Good', color: 'bg-yellow-500' };
    if (score <= 4) return { score: 4, label: 'Strong', color: 'bg-green-500' };
    return { score: 5, label: 'Very Strong', color: 'bg-green-600' };
  };

  const strength = getStrength(password);

  return (
    <div className="mt-2">
      <div className="flex gap-1 mb-1">
        {[1, 2, 3, 4, 5].map((level) => (
          <div
            key={level}
            className={`h-1 flex-1 rounded-full transition-all ${
              level <= strength.score ? strength.color : 'bg-gray-200'
            }`}
          />
        ))}
      </div>
      <p className={`text-xs ${strength.score <= 2 ? 'text-red-500' : 'text-gray-500'}`}>
        {strength.label}
      </p>
    </div>
  );
};

export default PasswordEditor;
